/**
 * Emphasis of what makes each anchor different.
 *
 * The three anchors of a behavior share most of their wording — what a rater
 * has to notice is the part that is *not* shared. Rather than asking content
 * authors to mark that by hand for 216 anchors, we compute it: any word that
 * appears in both of the other anchors, or is a stop word, is quiet; the rest
 * is the differentiator.
 *
 * Ported from the approved prototypes, which is where this behaviour was
 * validated with the client.
 */

/** Function words carry no distinguishing meaning in either language. */
const STOP_WORDS = new Set(
  (
    'a al ante bajo con contra de del desde durante e el en entre hacia hasta la las le les lo ' +
    'los mas más o para por que se segun según sin so sobre su sus tras un una unos unas y aun ' +
    'aún cuando como cada ' +
    'the a an and or of to in on at for with from by as is are be their them they it its when ' +
    'even while than that this those these not no'
  ).split(' '),
);

const WORD = /[\p{L}\p{N}]+/gu;

const normalise = (word: string): string =>
  word
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

export type HighlightSegment = {
  text: string;
  /** True when this run of words distinguishes the anchor from its siblings. */
  distinguishing: boolean;
};

/**
 * Split `text` into runs, marking those that set it apart from `others`.
 *
 * Returns segments rather than HTML so React renders them — no
 * dangerouslySetInnerHTML on content that is editable by a client admin.
 */
export const highlightDifference = (
  text: string,
  others: string[],
): HighlightSegment[] => {
  const otherWordSets = others.map(
    (other) => new Set((other.match(WORD) ?? []).map(normalise)),
  );

  const parts = text.split(/(\s+|[.,;:—-])/);
  const segments: HighlightSegment[] = [];
  let current: HighlightSegment | null = null;

  for (const part of parts) {
    if (part === '') continue;

    const isWord = new RegExp(`^${WORD.source}$`, 'u').test(part);

    if (!isWord) {
      // Punctuation and spacing stay with whatever run they follow, so a
      // highlighted phrase does not break across its own commas.
      if (current) current.text += part;
      else segments.push({ text: part, distinguishing: false });
      continue;
    }

    const word = normalise(part);
    const shared =
      STOP_WORDS.has(word) || otherWordSets.every((wordSet) => wordSet.has(word));
    const distinguishing = !shared;

    if (current && current.distinguishing === distinguishing) {
      current.text += part;
    } else {
      current = { text: part, distinguishing };
      segments.push(current);
    }
  }

  return segments;
};
